/**
 * The Analyst's spec language: the typed artifacts the agent produces and
 * pinned cards store. The invariant the whole feature hangs on: a pinned
 * card stores a SOURCE (how to get rows) and a CHART (how to draw them),
 * both plain JSON — refreshing a card re-executes the source against live
 * data and never involves the model.
 *
 * Two source tiers:
 * - `aggregation`: the typed engine — semantics baked in (homeNetAmount,
 *   payments-only default), validated forever, robust to refactors.
 * - `pipeline`: a guarded MongoDB aggregation pipeline for questions the
 *   engine never anticipated. Power tool; the server injects user scoping
 *   and rejects write/escape stages before execution.
 *
 * Zod is the single source of truth: the server validates specs with these
 * schemas, the agent's tool definitions are generated from them
 * (z.toJSONSchema), and the frontend gets the inferred types.
 */

// Namespace import deliberately: zod's ESM entry re-exports `z` as a
// namespace binding (`import * as z; export {z}`), which Parcel's scope
// hoisting mangles at runtime. The package also `export *`s every builder
// top-level, so the namespace form is equivalent and bundles correctly.
import * as z from 'zod';
import {parseDate} from './date';

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

/**
 * Relative windows stay relative in storage — that is what keeps a pinned
 * card live: "trailing-12mo" re-resolves against today on every refresh.
 */
export const relativeWindowSchema = z.object({
  kind: z.literal('relative'),
  preset: z.enum([
    'trailing-30d',
    'trailing-90d',
    'trailing-12mo',
    'trailing-24mo',
    'ytd',
    'prev-year',
    'all-time',
  ]),
});

export const absoluteWindowSchema = z
  .object({
    kind: z.literal('absolute'),
    /** ISO dates, inclusive. */
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  })
  .superRefine((window, ctx) => {
    // Shape alone admits 2026-02-30; a card must fail validation, not
    // silently return an empty window.
    if (!parseDate(window.startDate).isValid) {
      ctx.addIssue({code: 'custom', message: `Invalid date: ${window.startDate}`});
    }
    if (!parseDate(window.endDate).isValid) {
      ctx.addIssue({code: 'custom', message: `Invalid date: ${window.endDate}`});
    }
    if (window.startDate > window.endDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'startDate must be on or before endDate',
      });
    }
  });

export const analystWindowSchema = z.discriminatedUnion('kind', [
  relativeWindowSchema,
  absoluteWindowSchema,
]);
export type AnalystWindow = z.infer<typeof analystWindowSchema>;

// ---------------------------------------------------------------------------
// Aggregation spec (tier 1)
// ---------------------------------------------------------------------------

export const aggregationFiltersSchema = z
  .object({
    /** Category ids; the null sentinel 'uncategorized' matches rows with no category. */
    categoryIds: z.array(z.string()).optional(),
    /** Category group ids (from Settings.categoryGroups); expanded to members. */
    categoryGroupIds: z.array(z.string()).optional(),
    tagNames: z.array(z.string()).optional(),
    /** Case-insensitive substring match on payee. */
    payeeContains: z.string().optional(),
    /** Exact payee names. */
    payees: z.array(z.string()).optional(),
    /** Enum/text field equality: dimensionValues[fieldId] === value. */
    fieldEquals: z
      .object({fieldId: z.string(), value: z.string()})
      .optional(),
    /**
     * Which transactions participate. Payments (netAmount !== 0) is the
     * default and almost always right — transfers are money movement, not
     * spending or income.
     */
    transactionKind: z.enum(['payments', 'transfers', 'all']).optional(),
  })
  .optional();

export const aggregationGroupBySchema = z.discriminatedUnion('dimension', [
  z.object({
    dimension: z.enum([
      'category',
      'categoryGroup',
      'payee',
      'tag',
      'month',
      'year',
      'weekday',
    ]),
  }),
  // fieldValue requires the field id — without it every transaction would
  // silently group as '(unset)'.
  z.object({
    dimension: z.literal('fieldValue'),
    fieldId: z.string().min(1),
  }),
]);
export type AggregationGroupBy = z.infer<typeof aggregationGroupBySchema>;

const groupIdentity = (dim: AggregationGroupBy): string =>
  dim.dimension === 'fieldValue'
    ? `fieldValue:${dim.fieldId}`
    : dim.dimension;

/**
 * Plain object shape, exported separately because refined schemas cannot
 * be `.omit()`-ed — the agent derives its tool-input schema (minus `kind`)
 * from this and re-applies `topNRequiresGroupBy`.
 */
export const aggregationSpecObjectSchema = z.object({
  kind: z.literal('aggregation'),
  window: analystWindowSchema,
  filters: aggregationFiltersSchema,
  /**
   * Zero, one, or two dimensions. Zero = a single total row. Two enables
   * shapes like category × month. Grouping by 'tag' fans a multi-tag
   * transaction into each of its tags — sums across tag rows can exceed
   * the filtered total.
   */
  groupBy: z
    .array(aggregationGroupBySchema)
    .max(2)
    .optional()
    .refine(
      (dims) =>
        dims == null ||
        new Set(dims.map(groupIdentity)).size === dims.length,
      {
        message:
          'groupBy dimensions must be distinct (two fieldValue dimensions need different fieldIds)',
      },
    ),
  /**
   * All metrics are computed over homeNetAmount (USD cents, signed:
   * negative = money out). 'sum' is the default.
   */
  metric: z.enum(['sum', 'count', 'avg']).optional(),
  /** Keep the largest |value| rows, folding the rest into an 'other' row. */
  topN: z.number().int().min(1).max(100).optional(),
});

/** topN folds rows of the first dimension — without one it is meaningless. */
export const topNRequiresGroupBy = {
  check: (spec: {topN?: number; groupBy?: unknown[]}): boolean =>
    spec.topN == null || (spec.groupBy?.length ?? 0) > 0,
  message: 'topN requires at least one groupBy dimension',
};

export const aggregationSpecSchema = aggregationSpecObjectSchema.refine(
  topNRequiresGroupBy.check,
  {message: topNRequiresGroupBy.message},
);
export type AggregationSpec = z.infer<typeof aggregationSpecSchema>;

// ---------------------------------------------------------------------------
// Pipeline spec (tier 2)
// ---------------------------------------------------------------------------

export const ANALYST_COLLECTIONS = [
  'transactions',
  'rawtransactions',
  'payrollRecords',
  'accounts',
  'categories',
  'tags',
  'fields',
  'portfoliosnapshots',
] as const;
export type AnalystCollection = (typeof ANALYST_COLLECTIONS)[number];

export const pipelineSpecSchema = z.object({
  kind: z.literal('pipeline'),
  collection: z.enum(ANALYST_COLLECTIONS),
  /**
   * MongoDB aggregation stages. The server prepends user scoping, walks the
   * whole document rejecting write/escape operators, and guards $lookup —
   * see server/src/services/analyst/pipelineGuard.ts.
   */
  pipeline: z.array(z.record(z.string(), z.unknown())).min(1).max(30),
});
export type PipelineSpec = z.infer<typeof pipelineSpecSchema>;

export const cardSourceSchema = z.discriminatedUnion('kind', [
  aggregationSpecSchema,
  pipelineSpecSchema,
]);
export type CardSource = z.infer<typeof cardSourceSchema>;

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

/**
 * How rows bind to a picture. `x`/`y`/`series` name fields of the source's
 * result rows. Values in cents render through the app's formatters.
 */
export const chartSpecSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('line'),
    x: z.string(),
    y: z.string(),
    /** Optional field whose values split rows into one line each. */
    series: z.string().optional(),
    /** Display-only sign flip — spending is negative; negate so it plots up. */
    negate: z.boolean().optional(),
    /** How y values render: 'cents' (default) as dollars, 'number' raw — use for count metrics. */
    format: z.enum(['cents', 'number']).optional(),
  }),
  z.object({
    type: z.literal('bars'),
    x: z.string(),
    y: z.string(),
    horizontal: z.boolean().optional(),
    negate: z.boolean().optional(),
    format: z.enum(['cents', 'number']).optional(),
  }),
  z.object({
    type: z.literal('stackedArea'),
    x: z.string(),
    y: z.string(),
    series: z.string(),
    negate: z.boolean().optional(),
    format: z.enum(['cents', 'number']).optional(),
  }),
  z.object({
    type: z.literal('stat'),
    /** Field holding the single value (first row is used). */
    value: z.string(),
    /** 'cents' renders $; 'number' renders the raw value. */
    format: z.enum(['cents', 'number']).optional(),
  }),
  z.object({
    type: z.literal('table'),
    columns: z.array(z.string()).min(1),
    /** Column names (subset of `columns`) holding cent amounts. */
    centsColumns: z.array(z.string()).optional(),
  }),
]);
export type ChartSpec = z.infer<typeof chartSpecSchema>;

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export const cardSpecSchema = z.object({
  title: z.string().min(1).max(120),
  /** One or two sentences of what the card shows / why it matters. */
  commentary: z.string().max(600).optional(),
  source: cardSourceSchema,
  chart: chartSpecSchema,
});
export type CardSpec = z.infer<typeof cardSpecSchema>;

/** A row set produced by executing a card source. */
export type AnalystRows = Record<string, unknown>[];
